import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NewGoalSheet from '../components/NewGoalSheet';
import { useGoals } from '../contexts/GoalsContext';
import { useMomentum } from '../contexts/MomentumContext';
import Settings from '../components/Settings';
import GoalSheet from '../components/GoalSheet';
import CircularProgress from '../components/CircularProgress';
import { useReflections } from '../contexts/ReflectionsContext';
import { moods } from '../constants/moods';
import { useNotifications } from '../contexts/NotificationsContext';

const dailyQuotes = [
  {
    text: "Every moment is a fresh beginning.",
    author: "T.S. Eliot"
  },
  {
    text: "The secret of getting ahead is getting started.",
    author: "Mark Twain"
  },
  {
    text: "The only way to do great work is to love what you do.",
    author: "Steve Jobs"
  },
  {
    text: "Small progress is still progress.",
    author: "Anonymous"
  },
  {
    text: "The journey of a thousand miles begins with one step.",
    author: "Lao Tzu"
  },
  {
    text: "Don't count the days, make the days count.",
    author: "Muhammad Ali"
  },
  {
    text: "The future depends on what you do today.",
    author: "Mahatma Gandhi"
  },
  {
    text: "Your time is limited, don't waste it living someone else's life.",
    author: "Steve Jobs"
  },
  {
    text: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
    author: "Winston Churchill"
  },
  {
    text: "The best way to predict the future is to create it.",
    author: "Peter Drucker"
  }
];

const analyzeMood = (reflections) => {
  if (!reflections?.length) return null;

  // Get reflections from the last 7 days
  const lastWeekReflections = reflections.filter(reflection => {
    const reflectionDate = new Date(reflection.timestamp);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return reflectionDate >= weekAgo;
  });

  if (!lastWeekReflections.length) return null;

  // Count mood frequencies
  const moodCounts = lastWeekReflections.reduce((acc, reflection) => {
    acc[reflection.mood.name] = (acc[reflection.mood.name] || 0) + 1;
    return acc;
  }, {});

  // Find the most frequent mood
  const dominantMood = Object.entries(moodCounts).reduce((a, b) => 
    moodCounts[a[0]] > moodCounts[b[0]] ? a : b
  )[0];

  // Calculate percentages
  const totalEntries = lastWeekReflections.length;
  const moodPercentages = Object.entries(moodCounts).map(([mood, count]) => ({
    mood,
    percentage: Math.round((count / totalEntries) * 100)
  })).sort((a, b) => b.percentage - a.percentage);

  return {
    dominantMood,
    moodPercentages,
    totalEntries
  };
};

export default function Dashboard() {
  const { currentUser } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [showNewGoalModal, setShowNewGoalModal] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { goals, loading, updateGoalProgress } = useGoals();
  const { streak, loading: momentumLoading } = useMomentum();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showGoalSheet, setShowGoalSheet] = useState(false);
  const { reflections } = useReflections();
  const moodAnalysis = analyzeMood(reflections);
  const { 
    requestNotificationPermission,
    permissionStatus  // Keep this if it's being used
  } = useNotifications();

  const dailyQuote = (() => {
    const today = new Date().toDateString();
    const seed = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const index = seed % dailyQuotes.length;
    return dailyQuotes[index];
  })();

  // Generate avatar URL using DiceBear's "adventurer-neutral" collection
  const avatarUrl = currentUser?.email 
    ? `https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${encodeURIComponent(currentUser.email)}&mouth=variant01,variant27`
    : `https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=default&mouth=variant01,variant27`;

  const quickActions = [
    {
      name: 'New Goal',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      onClick: () => {
        setSelectedGoal(null);
        setShowGoalSheet(true);
      }
    },
    {
      name: 'Journal',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => navigate('/reflection')
    },
    {
      name: 'Friends',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
          />
        </svg>
      ),
      onClick: () => navigate('/friends')
    },
    {
      name: 'Settings',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      onClick: () => setIsSettingsOpen(true)
    }
  ];

  const handleNotificationPermission = async () => {
    const result = await requestNotificationPermission();
    switch (result) {
      case 'granted':
        // Permission was granted, notification will be sent automatically
        break;
      case 'denied':
        // Could show a toast message here about enabling in browser settings
        console.log('Notifications denied. Please enable them in your browser settings.');
        break;
      case 'unsupported':
        console.log('Your browser does not support notifications.');
        break;
      default:
        // User closed the prompt without choosing
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-1 pb-20 relative overflow-hidden no-select">
      {/* Theme Background Circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-[0.15] blur-3xl animate-float pointer-events-none"
          style={{ 
            background: `radial-gradient(circle, ${currentTheme?.colors?.primary?.[400] || '#f97316'} 0%, transparent 70%)`,
            animationDelay: '0s'
          }} 
        />
        <div 
          className="absolute top-1/3 -left-32 w-[500px] h-[500px] rounded-full opacity-[0.15] blur-3xl animate-float-delayed pointer-events-none"
          style={{ 
            background: `radial-gradient(circle, ${currentTheme?.colors?.primary?.[300] || '#fdba74'} 0%, transparent 70%)`,
            animationDelay: '-4s'
          }} 
        />
        <div 
          className="absolute -bottom-40 right-20 w-[600px] h-[600px] rounded-full opacity-[0.15] blur-3xl animate-float pointer-events-none"
          style={{ 
            background: `radial-gradient(circle, ${currentTheme?.colors?.primary?.[500] || '#ea580c'} 0%, transparent 70%)`,
            animationDelay: '-2s'
          }} 
        />
        <div 
          className="absolute top-20 right-1/4 w-72 h-72 rounded-full opacity-[0.15] blur-3xl animate-float-delayed pointer-events-none"
          style={{ 
            background: `radial-gradient(circle, ${currentTheme?.colors?.primary?.[400] || '#f97316'} 0%, transparent 70%)`,
            animationDelay: '-6s'
          }} 
        />
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6 relative z-10">
        {/* Welcome Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <img 
              src={avatarUrl} 
              alt="Profile" 
              className="w-16 h-16 rounded-full bg-primary-50"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Welcome back, {currentUser?.displayName || 'Friend'}
              </h1>
              <p className="mt-1 text-gray-600">Ready for a fresh start?</p>
            </div>
          </div>
        </div>

        {/* Only show notification prompt if permissions aren't granted */}
        {permissionStatus !== 'granted' && (
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Enable Notifications
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Get daily reminders to stay on track with your goals and maintain your journaling habit.
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleNotificationPermission}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    Enable Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Quote */}
        <div className="bg-primary-50 rounded-2xl p-6">
          <p className="text-primary-800 font-medium italic">"{dailyQuote.text}"</p>
          <p className="text-primary-600 text-sm mt-2">— {dailyQuote.author}</p>
        </div>

        {/* Mood Analysis */}
        {moodAnalysis && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Your Mood Analysis</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Past 7 days dominant mood</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl">
                      {moods.find(m => m.name === moodAnalysis.dominantMood)?.emoji}
                    </span>
                    <span className="font-medium text-gray-800">
                      {moodAnalysis.dominantMood}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Based on</p>
                  <p className="font-medium text-gray-800">{moodAnalysis.totalEntries} entries</p>
                </div>
              </div>

              {/* Mood Distribution */}
              <div className="space-y-2">
                {moodAnalysis.moodPercentages.map(({ mood, percentage }) => (
                  <div key={mood} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-2">
                        {moods.find(m => m.name === mood)?.emoji} {mood}
                      </span>
                      <span className="text-gray-700 font-medium">{percentage}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary-500 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <button
                key={action.name}
                onClick={action.onClick}
                className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all hover:scale-105 active:scale-95 z-10"
              >
                <div className="text-primary-600">{action.icon}</div>
                <span className="text-xs font-medium text-gray-600 mt-2">{action.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Current Goals */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Current Goals</h2>
          <div className="space-y-4">
            {loading ? (
              // Loading skeleton
              [...Array(3)].map((_, i) => (
                <div key={i} className="bg-white p-4 rounded-xl shadow-sm animate-pulse">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 w-48 bg-gray-200 rounded"></div>
                      <div className="h-3 w-24 bg-gray-200 rounded"></div>
                    </div>
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
              ))
            ) : goals.length === 0 ? (
              // Empty state
              <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                <div className="text-4xl mb-2">🎯</div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No goals yet</h3>
                <p className="text-gray-500 mb-4">Start by creating your first goal!</p>
                <button
                  onClick={() => setShowNewGoalModal(true)}
                  className="text-primary-600 font-medium hover:text-primary-700"
                >
                  + Add Goal
                </button>
              </div>
            ) : (
              // Goals list
              goals.map((goal) => (
                <div 
                  key={goal.id} 
                  className="bg-white p-4 rounded-xl shadow-sm group cursor-pointer hover:shadow-md transition-all"
                  onClick={() => {
                    setSelectedGoal(goal);
                    setShowGoalSheet(true);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800">{goal.title}</h3>
                      <span className="text-xs text-gray-500">{goal.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Progress Controls */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateGoalProgress(goal.id, Math.max(0, goal.progress - 5));
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateGoalProgress(goal.id, Math.min(100, goal.progress + 5));
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                      <CircularProgress progress={goal.progress} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Momentum Streak */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Your Momentum</h2>
            <span className="text-primary-600 font-bold text-2xl">{streak}</span>
          </div>
          <div className="flex justify-between">
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  i < streak 
                    ? 'bg-primary-100 text-primary-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                <span className="text-sm font-medium">
                  {i < streak ? '✓' : ''}
                </span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            {momentumLoading ? 'Calculating streak...' : 
              streak === 0 ? 'Start your streak by making progress on your goals!' :
              streak === 1 ? "You're off to a great start!" :
              `You're on a ${streak}-day streak! Keep going!`
            }
          </p>
        </div>
      </div>

      {/* Add Settings component */}
      <Settings 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />

      {/* New Goal Sheet */}
      <NewGoalSheet 
        isOpen={showNewGoalModal} 
        onClose={() => setShowNewGoalModal(false)} 
      />

      {/* Goal Sheet */}
      <GoalSheet 
        isOpen={showGoalSheet}
        onClose={() => {
          setShowGoalSheet(false);
          setSelectedGoal(null);
        }}
        goal={selectedGoal}
      />
    </div>
  );
} 